// Firebase v9+ Modular SDK Configuration with Optimized Persistent Cache
// Firestore is decoupled from Auth to allow public data fetching without waiting for auth
import { initializeApp } from 'firebase/app';
import { initializeFirestore } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { getDatabase } from 'firebase/database';

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyBkLEz6ADgOx78QEJDrJrd1Lnepuwqii0g",
  authDomain: "varaha-silks.firebaseapp.com",
  projectId: "varaha-silks",
  storageBucket: "varaha-silks.firebasestorage.app",
  messagingSenderId: "812217696139",
  appId: "1:812217696139:web:83372cb58b290576de798e",
  databaseURL: "https://varaha-silks-default-rtdb.asia-southeast1.firebasedatabase.app"
};

// Initialize Firebase v9+
let app = null;
let db = null;
let auth = null;
let storage = null;
let realtimeDb = null;

// CRITICAL: Suppress harmless Firestore WebChannel 404 errors
// These are expected during connection establishment and don't affect functionality
if (typeof window !== 'undefined') {
  const originalError = console.error;
  console.error = (...args) => {
    // Filter out harmless Firestore WebChannel 404 errors
    const errorString = args.join(' ');
    if (
      errorString.includes('firestore.googleapis.com') &&
      errorString.includes('Listen/channel') &&
      (errorString.includes('404') || errorString.includes('Not Found'))
    ) {
      // Suppress these harmless connection errors - they're expected during WebChannel establishment
      return;
    }
    // Log all other errors normally
    originalError.apply(console, args);
  };
}

// Initialize Firestore independently (decoupled from Auth)
// This allows public data to load immediately without waiting for auth initialization
try {
  // Initialize Firebase app
  app = initializeApp(firebaseConfig);

  // Initialize Firestore with long polling to avoid WebChannel Listen errors
  db = initializeFirestore(app, {
    experimentalForceLongPolling: true
  });
  console.log('✅ Firestore initialized with long polling');
  console.log('✅ Firestore ready for queries:', {
    hasDb: !!db,
    dbType: typeof db,
    collection: typeof db.collection === 'function' ? 'available' : 'not available'
  });
} catch (error) {
  // Only log critical initialization errors in development
  if (process.env.NODE_ENV === 'development') {
    console.error('Firebase app initialization error:', error);
  }
  app = null;
  db = null;
}

// Initialize Auth separately (non-blocking for Firestore)
// Auth initialization happens independently, allowing Firestore to work immediately
try {
  if (app) {
    auth = getAuth(app);
  }
} catch (authError) {
  if (process.env.NODE_ENV === 'development') {
    console.warn('Auth initialization error (non-blocking):', authError);
  }
  // Auth errors don't block Firestore
}

// Initialize Storage separately (non-blocking)
// CRITICAL: Storage must be initialized for Blaze plan image uploads
try {
  if (app) {
    storage = getStorage(app);
    console.log('✅ Firebase Storage initialized successfully:', {
      hasStorage: !!storage,
      storageType: typeof storage,
      bucket: firebaseConfig.storageBucket
    });
  } else {
    console.error('❌ Cannot initialize Storage: Firebase app not initialized');
  }
} catch (storageError) {
  console.error('❌ Storage initialization error:', storageError);
  console.error('Storage error details:', {
    message: storageError.message,
    code: storageError.code,
    stack: storageError.stack
  });
  // Set storage to null explicitly
  storage = null;
}

// Initialize Realtime Database separately (non-blocking)
try {
  if (app) {
    realtimeDb = getDatabase(app);
  }
} catch (realtimeError) {
  if (process.env.NODE_ENV === 'development') {
    console.warn('Realtime DB initialization error (non-blocking):', realtimeError);
  }
}

// Export Firebase services
export { db, auth, storage, realtimeDb };
export default app;

// Check if Firestore is initialized (decoupled from Auth check)
// This allows public data fetching without waiting for auth
export const isFirebaseInitialized = () => {
  const initialized = !!(app && db);
  if (!initialized) {
    console.warn('⚠️ Firebase not initialized:', {
      hasApp: !!app,
      hasDb: !!db,
      appType: app ? typeof app : 'null',
      dbType: db ? typeof db : 'null'
    });
  }
  return initialized;
};

// Check if Auth is initialized separately
export const isAuthInitialized = () => {
  return !!(app && auth);
};

// Check if Storage is initialized
export const isStorageInitialized = () => {
  return !!(app && storage);
};

// Ensure Storage is initialized
export const ensureStorageInitialized = async () => {
  console.log('🔍 ensureStorageInitialized called:', {
    hasStorage: !!storage,
    hasApp: !!app,
    storageBucket: firebaseConfig.storageBucket
  });

  if (storage) {
    console.log('✅ Storage already initialized');
    return storage;
  }

  if (!app) {
    const errorMsg = 'Firebase app not initialized. Cannot initialize Storage.';
    console.error('❌', errorMsg);
    throw new Error(errorMsg);
  }

  try {
    console.log('⏳ Initializing Firebase Storage...');
    storage = getStorage(app);
    console.log('✅ Storage initialized successfully:', {
      hasStorage: !!storage,
      storageType: typeof storage,
      bucket: firebaseConfig.storageBucket
    });
    return storage;
  } catch (error) {
    console.error('❌ Failed to initialize Storage:', error);
    console.error('Error details:', {
      message: error.message,
      code: error.code,
      stack: error.stack
    });

    // Provide helpful error message
    const errorMsg = `Firebase Storage initialization failed: ${error.message}. ` +
      `Please ensure:\n` +
      `1. You are on the Firebase Blaze (pay-as-you-go) plan\n` +
      `2. Firebase Storage is enabled in your Firebase Console\n` +
      `3. Storage bucket "${firebaseConfig.storageBucket}" exists\n` +
      `4. Storage rules are properly configured`;
    throw new Error(errorMsg);
  }
};

// Get Firebase services
export const getFirebaseServices = () => {
  return {
    db: db || null,
    auth: auth || null,
    storage: storage || null,
    realtimeDb: realtimeDb || null
  };
};

